import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

let isValidating = false;

// Function to validate the cart items and remove orphaned promotional items
async function validateCart(items) {
  if (isValidating) return;

  // We check for items containing bundle metadata in line item properties:
  // _bundle_id, _bundle_role (e.g. 'main', 'free')
  const groups = {};

  items.forEach((item, index) => {
    // Shopify line item indexes are 1-based
    const lineIndex = index + 1;
    const properties = item.properties || {};
    const bundleId = properties._bundle_id;
    const bundleRole = properties._bundle_role;

    if (bundleId) {
      if (!groups[bundleId]) {
        groups[bundleId] = {
          main: [],
          free: []
        };
      }
      if (bundleRole === 'main') {
        groups[bundleId].main.push({ item, lineIndex });
      } else if (bundleRole === 'free') {
        groups[bundleId].free.push({ item, lineIndex });
      }
    }
  });

  const updates = {};
  let needsUpdate = false;

  // Validate each bundle group
  for (const bundleId in groups) {
    const group = groups[bundleId];
    const totalMainQty = group.main.reduce((sum, entry) => sum + entry.item.quantity, 0);

    // If there is no main product in the cart for this bundle, remove all free items belonging to this bundle
    if (totalMainQty <= 0 && group.free.length > 0) {
      group.free.forEach(entry => {
        updates[entry.item.key] = 0;
        needsUpdate = true;
      });
    } else if (group.free.length > 0) {
      // E.g. limit free item quantity based on main product quantity
      // If we want a strict checkout validation where free item quantity cannot exceed a certain ratio (e.g. 1 free per 2 main)
      // group.free.forEach(entry => {
      //   const maxAllowedFree = Math.floor(totalMainQty / 2);
      //   if (entry.item.quantity > maxAllowedFree) {
      //     updates[entry.item.key] = maxAllowedFree;
      //     needsUpdate = true;
      //   }
      // });
    }
  }

  if (needsUpdate) {
    isValidating = true;
    try {
      const sectionsToUpdate = new Set();
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset.sectionId) {
          sectionsToUpdate.add(el.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        updates: updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      const parsedCart = await response.json();

      // Dispatch CartLinesUpdateEvent to morph cart components
      const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: Object.keys(updates).map(key => ({ id: key, quantity: updates[key] })),
          promise: deferredUpdatePromise.promise
        })
      );

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedCart),
        detail: {
          sections: parsedCart.sections,
          items: parsedCart.items,
          itemCount: parsedCart.item_count,
          source: 'cart-bundle-validation',
          didError: false
        }
      });
    } catch (error) {
      console.error('[cart-bundle-validation] Validation request failed:', error);
    } finally {
      isValidating = false;
    }
  }
}

// Listen to all cart updates
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ detail }) => {
      // Skip if this event was dispatched by our validator to prevent loops
      if (detail?.source === 'cart-bundle-validation') return;

      if (detail?.items) {
        validateCart(detail.items);
      } else {
        // Fallback: fetch current cart
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => validateCart(cart.items));
      }
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[cart-bundle-validation] CartLinesUpdateEvent promise rejected:', error);
      }
    });
});

// Run once on page load (in case cart was loaded with invalid state)
window.addEventListener('DOMContentLoaded', () => {
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      if (cart && cart.items) {
        validateCart(cart.items);
      }
    })
    .catch(err => console.error('[cart-bundle-validation] Initial fetch failed:', err));
});
